import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import type { Session, SupabaseClient, User } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import {
	AuthContext,
	type AuthContextValue,
	UNAVAILABLE_AUTH_VALUE,
} from "./AuthContext";

interface AuthSnapshot {
	user: User | null;
	session: Session | null;
	loading: boolean;
}

interface AuthProviderProps {
	children: ReactNode;
	client?: SupabaseClient | null;
}

export function AuthProvider({ children, client = supabase }: AuthProviderProps) {
	if (!client) {
		return (
			<AuthContext.Provider value={UNAVAILABLE_AUTH_VALUE}>
				{children}
			</AuthContext.Provider>
		);
	}

	return (
		<ConfiguredAuthProvider client={client}>{children}</ConfiguredAuthProvider>
	);
}

function ConfiguredAuthProvider({
	children,
	client,
}: {
	children: ReactNode;
	client: SupabaseClient;
}) {
	const [snapshot, setSnapshot] = useState<AuthSnapshot>({
		user: null,
		session: null,
		loading: true,
	});

	useEffect(() => {
		let active = true;
		let authRevision = 0;

		const {
			data: { subscription },
		} = client.auth.onAuthStateChange((_event, session) => {
			authRevision += 1;
			if (!active) return;
			setSnapshot({
				user: session?.user ?? null,
				session,
				loading: false,
			});
		});

		const bootstrapRevision = authRevision;
		void client.auth
			.getSession()
			.then(({ data: { session } }) => {
				if (!active || authRevision !== bootstrapRevision) return;
				setSnapshot({
					user: session?.user ?? null,
					session,
					loading: false,
				});
			})
			.catch(() => {
				if (!active || authRevision !== bootstrapRevision) return;
				setSnapshot({ user: null, session: null, loading: false });
			});

		return () => {
			active = false;
			subscription.unsubscribe();
		};
	}, [client]);

	const signUp = useCallback(
		async (email: string, password: string) => {
			const { error } = await client.auth.signUp({ email, password });
			return { error: error?.message ?? null };
		},
		[client],
	);

	const signIn = useCallback(
		async (email: string, password: string) => {
			const { error } = await client.auth.signInWithPassword({ email, password });
			return { error: error?.message ?? null };
		},
		[client],
	);

	const signOut = useCallback(async () => {
		await client.auth.signOut();
	}, [client]);

	const value = useMemo<AuthContextValue>(
		() => ({
			...snapshot,
			authAvailable: true,
			isSuperuser: snapshot.user?.app_metadata?.is_superuser === true,
			signUp,
			signIn,
			signOut,
		}),
		[snapshot, signUp, signIn, signOut],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
